import type { KeyboardEvent } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import { Modal } from "newui/components/ui/Modal";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { PasswordEntry, PASSWORD_RULES } from "newui/features/Auth/components/PasswordEntry/PasswordEntry";
import { useAppDispatch } from "newui/store/hooks";
import { changePasswordActions } from "actions/AccountManagementActions";

export interface ChangePasswordModalProps {
	isOpen: boolean;
	userId: number;
	onClose: () => void;
}

interface ChangePasswordFormValues {
	current_password: string;
	password: string;
	password_confirmation: string;
}

const INITIAL_VALUES: ChangePasswordFormValues = {
	current_password: "",
	password: "",
	password_confirmation: "",
};

/**
 * PRO-731 — Change Password, now presented inside a modal instead of inline
 * on the My Profile page.
 *
 * Field names (`password` / `password_confirmation`) come straight from
 * `PasswordEntry`'s own contract, which also happens to be what the backend's
 * `UpdatePasswordRequest` validates against — the legacy inline form on
 * EditUserProfile.js submitted `confirmationPassword` (no underscore), which
 * does not match that rule and would 422. Reusing `PasswordEntry` as-is fixes
 * that mismatch as a side effect of following the coding standard.
 */
export const ChangePasswordModal = ({ isOpen, userId, onClose }: ChangePasswordModalProps) => {
	const dispatch = useAppDispatch();

	const handleSubmit = async (
		values: ChangePasswordFormValues,
		helpers: FormikHelpers<ChangePasswordFormValues>
	) => {
		const passwordChanged = await dispatch(changePasswordActions(values, userId));
		if (passwordChanged) {
			helpers.resetForm();
			onClose();
		}
	};

	return (
		<Formik
			enableReinitialize
			initialValues={INITIAL_VALUES}
			onSubmit={handleSubmit}
		>
			{({ isValid, isSubmitting, resetForm, submitForm }) => {
				const handleCancel = () => {
					resetForm();
					onClose();
				};

				// PRO-731 follow-up — the primary "Change Password" button lives in
				// the shared Modal's footer, which is a sibling of this `<Form>`, not
				// a descendant of it, so there is no `type="submit"` control inside
				// the form for the browser to submit on Enter. Handle it explicitly
				// instead (and preventDefault so it can't also fall through to a
				// native, un-handled submit) — guarded the same way the button
				// itself is (`isValid`/`isSubmitting`) so Enter can't double-submit
				// while a request is already in flight.
				const handleFormKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
					if (e.key !== "Enter" || (e.target as HTMLElement).tagName !== "INPUT") {
						return;
					}
					e.preventDefault();
					if (isValid && !isSubmitting) {
						submitForm();
					}
				};

				return (
					<Modal
						isOpen={isOpen}
						onClose={handleCancel}
						title="Change Password"
						description="This is your personal password linked to your Procuracon account."
						iconType="edit"
						size="md"
						// PRO-731 bug C — Cancel on the left, primary action on the right,
						// matching the approved design and the convention used by other
						// hand-rolled modal footers in this codebase (e.g.
						// KeyPersonnelFormPage's delete-confirm footer).
						secondaryButtonFirst
						primaryButton={{
							text: "Change Password",
							onClick: submitForm,
							disabled: !isValid || isSubmitting,
							isLoading: isSubmitting,
							// PRO-731 bug B — "Change Password" doesn't fit the modal
							// footer's default 100px button, so it already wraps onto two
							// lines; adding the loading Spinner alongside it then narrows
							// the remaining space further and reflows/shrinks the button.
							// A min-width wide enough for label + spinner (min-width wins
							// over the narrower fixed width per CSS clamping) plus
							// whitespace-nowrap keeps the button's WIDTH identical in both
							// states.
							//
							// HEIGHT also grew while loading (45px -> 54px): Button's
							// isLoading branch renders Chakra's <Spinner /> with no `size`,
							// so it defaults to theme size "md" (`sizes.6` = 1.5rem/24px) —
							// taller than the button's own text line-box (0.75rem font *
							// 1.5 line-height = 18px) — so the flex row's cross-axis
							// (align-items: center) grows to fit it. Confirmed against
							// @chakra-ui/theme's spinner theme (defaultProps.size = "md",
							// sizes.md = "sizes.6") and Button.tsx's `<Spinner />` call.
							// Overriding just the spinner rendered inside THIS button to
							// theme size "sm" (`sizes.4` = 1rem/16px, back under the text
							// line-box) via a scoped descendant selector avoids growing the
							// box at all, without touching Button.tsx's shared spinner
							// markup — every other `isLoading` button/`Spinner` in the app
							// (~17 other Modal primaryButton/secondaryButton callers plus
							// every direct `<Button isLoading>` use) is unaffected.
							className:
								"min-w-[200px] whitespace-nowrap [&_.chakra-spinner]:!h-4 [&_.chakra-spinner]:!w-4",
						}}
						secondaryButton={{
							text: "Cancel",
							onClick: handleCancel,
						}}
					>
						<Form data-testid="change-password-form" onKeyDown={handleFormKeyDown}>
							<div className="mb-4">
								<InstructionsInfoBox
									title="Password Rules..."
									instructions={PASSWORD_RULES}
									data-testid="password-rules-info-box"
								/>
							</div>
							<PasswordEntry
								passwordOverride={{
									id: "current_password",
									name: "current_password",
									label: "Current Password",
								}}
								passwordDataTestid="current-password-input"
							/>
							<PasswordEntry
								showConfirmPassword
								passwordDataTestid="new-password-input"
								passwordConfirmationDataTestid="confirm-password-input"
							/>
						</Form>
					</Modal>
				);
			}}
		</Formik>
	);
};
